import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { usePageLoading } from '../contexts/PageLoadingContext';

// --- MUI Imports ---
import {
    Container, Box, TextField, Button, Typography, Paper, Alert,
    FormControl, FormLabel, RadioGroup, FormControlLabel, Radio,
    FormGroup, Checkbox, CircularProgress, Grid, Tabs, Tab,
    InputAdornment, Divider, IconButton, Fade, useMediaQuery,
    useTheme, Avatar, Card, CardContent, Tooltip, Fab
} from '@mui/material';

// --- Icons ---
import BusinessIcon from '@mui/icons-material/Business';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

// --- Animation Component ---
import PageAnimation from '../components/animations/PageAnimation';
import { px } from 'framer-motion';

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`profile-tabpanel-${index}`}
            aria-labelledby={`profile-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: { xs: 2, md: 3 }, animation: 'fadeIn 0.5s ease-in-out' }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `profile-tab-${index}`,
        'aria-controls': `profile-tabpanel-${index}`,
    };
}

function CompleteRegistrationPage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    // --- State ---
    const [loadingData, setLoadingData] = useState(true);
    const { setPageLoading } = usePageLoading();

    // Report loading state to App.js for footer visibility
    useEffect(() => {
        setPageLoading(loadingData);
        return () => setPageLoading(false);
    }, [loadingData, setPageLoading]);
    const [tabValue, setTabValue] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // Form Data
    const [formData, setFormData] = useState({
        // Core Account Info
        businessName: '',
        email: '',
        password: '',
        contactPerson: '',
        phoneNumber: '',
        companyWebsite: '',
        businessAddress: '',
        // Business Details
        businessType: '',
        yearsOperating: '',
        numLocations: '1',
        estimatedVolume: '',
        // Logistics
        preferredDeliveryTimes: '',
        hasLoadingDock: '',
        specialDeliveryInstructions: '',
        // Service Needs
        priorities: {
            pricing: false,
            freshness: true,
            reliability: false,
            variety: false,
            customerService: false,
        },
        minQuantityRequirements: '',
        reasonForSwitch: '',
        // Billing
        paymentMethod: '',
        statement_duration: '',
        // Final Notes
        additionalNotes: '',
    });

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [phoneError, setPhoneError] = useState('');

    // --- Handlers ---
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setError('');
        setSuccess('');
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        if (name === 'phoneNumber') {
            if (value && (!value.startsWith('+') || value.length < 8)) {
                setPhoneError('Enter valid mobile number with country code (e.g., +1...)');
            } else {
                setPhoneError('');
            }
        }
    };

    const handleCheckboxChange = (e) => {
        setFormData({
            ...formData,
            priorities: {
                ...formData.priorities,
                [e.target.name]: e.target.checked,
            },
        });
    };

    // Fetch Data
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await api.get('/profile');
                const data = response.data;

                // If email is a placeholder, clear it so user must enter a real one
                if (data.email && data.email.endsWith('@mataxexpress.ca')) {
                    data.email = '';
                }

                setFormData(prev => ({
                    ...prev,
                    ...data,
                    password: '',
                    priorities: {
                        ...prev.priorities,
                        ...(data.priorities || {})
                    }
                }));
            } catch (err) {
                console.error("Failed to fetch profile:", err);
                setError("Failed to load your profile data.");
                if (err.response && err.response.status === 401) {
                    navigate('/login');
                }
            } finally {
                setLoadingData(false);
            }
        };

        fetchProfile();
    }, [navigate]);

    const handleSubmit = async () => {
        if (phoneError) {
            setError('Please fix errors before saving.');
            return;
        }

        setError('');
        setSuccess('');
        setSubmitting(true);

        try {
            const submissionData = { ...formData };
            if (!submissionData.password) {
                delete submissionData.password;
            }

            const response = await api.put('/complete-registration', submissionData);
            setSuccess(response.data.msg || 'Profile updated successfully!');

            if (response.data.new_token) {
                localStorage.setItem('accessToken', response.data.new_token);
                if (response.data.new_email) {
                    localStorage.setItem('email', response.data.new_email);
                }
                window.dispatchEvent(new Event('storage'));
            }

            // Check for completeness logic could go here
            // For now, just show success
            setTimeout(() => setSuccess(''), 4000);
        } catch (err) {
            setError(err.response?.data?.msg || 'Update failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#f4f6f8' }}>
                <CircularProgress sx={{ color: '#75a84b' }} />
            </Box>
        );
    }

    const getInitials = (name) => {
        return name ? name.charAt(0).toUpperCase() : 'U';
    };

    return (
        <PageAnimation>
            <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f8', pb: 8 }}>
                {/* --- Header Section --- */}
                <Paper
                    elevation={0}
                    sx={{
                        bgcolor: '#75a84b',
                        color: 'white',
                        pt: { xs: 4, md: 8 },
                        pb: { xs: 6, md: 10 },
                        px: 2,
                        mb: -4,
                        borderRadius: 0
                    }}
                >
                    <Container maxWidth="lg">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexDirection: { xs: 'column', sm: 'row' }, textAlign: { xs: 'center', sm: 'left' } }}>
                            <Avatar
                                sx={{
                                    width: 80,
                                    height: 80,
                                    bgcolor: 'white',
                                    color: '#75a84b',
                                    fontWeight: 'bold',
                                    fontSize: '2rem',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                }}
                            >
                                {getInitials(formData.contactPerson)}
                            </Avatar>
                            <Box>
                                <Typography variant="h4" fontWeight="bold">
                                    {formData.businessName || 'My Business'}
                                </Typography>
                                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                                    Manage your account settings and preferences
                                </Typography>
                            </Box>
                        </Box>
                    </Container>
                </Paper>

                {/* --- Main Content --- */}
                <Container maxWidth="lg">
                    <Grid container spacing={3}>
                        {/* --- Tabs Sidebar (Desktop) / Topbar (Mobile) --- */}
                        <Grid item xs={12} md={3}>
                            <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                <Tabs
                                    orientation={isMobile ? "horizontal" : "vertical"}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    value={tabValue}
                                    onChange={handleTabChange}
                                    sx={{
                                        borderRight: { md: 1, borderColor: 'divider' },
                                        '& .MuiTab-root': {
                                            alignItems: 'center',
                                            justifyContent: { md: 'flex-start' },
                                            minHeight: 60,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '0.95rem',
                                            gap: 1.5
                                        },
                                        '& .Mui-selected': {
                                            color: '#75a84b !important',
                                            bgcolor: '#f0f5e4'
                                        },
                                        '& .MuiTabs-indicator': {
                                            bgcolor: '#75a84b',
                                            left: 0,
                                            width: 4
                                        }
                                    }}
                                >
                                    <Tab icon={<PersonIcon />} label="Account Details" {...a11yProps(0)} />
                                    <Tab icon={<BusinessIcon />} label="Business Info" {...a11yProps(1)} />
                                    <Tab icon={<LocalShippingIcon />} label="Delivery" {...a11yProps(2)} />
                                    <Tab icon={<PaymentIcon />} label="Billing" {...a11yProps(3)} />
                                </Tabs>
                            </Paper>
                        </Grid>

                        {/* --- Form Content --- */}
                        <Grid item xs={12} md={9}>
                            <Paper elevation={2} sx={{ borderRadius: 2, minHeight: 400, maxWidth: '100%', position: 'relative' }}>
                                {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
                                {success && <Alert severity="success" sx={{ m: 2 }} icon={<CheckCircleIcon fontSize="inherit" />}>{success}</Alert>}

                                {/* --- Tab 1: Account Details --- */}
                                <TabPanel value={tabValue} index={0}>
                                    <Typography variant="h6" gutterBottom sx={{ color: '#75a84b', fontWeight: 'bold', mb: 3 }}>
                                        Basic Information
                                    </Typography>
                                    <Grid container spacing={3} sx={{ mb: 0 }}>

                                        <Grid item xs={12} sm={6} sx={{ mb: 0 }}>
                                            <TextField required fullWidth label="Contact Person" name="contactPerson" value={formData.contactPerson} onChange={handleChange} variant="outlined" />
                                        </Grid>
                                        <Grid item xs={12} sm={6} sx={{ mb: 3 }}>
                                            <TextField required fullWidth label="Business Name" name="businessName" value={formData.businessName} onChange={handleChange} variant="outlined" />
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} sm={6} sx={{ mb: 3 }}>
                                        <TextField required fullWidth label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} variant="outlined" />
                                        {(!formData.email || formData.email.endsWith('@mataxexpress.ca')) && (
                                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'red', fontSize: '0.75rem' }}>
                                                We highly recommend that you <Box component="span" sx={{ fontWeight: 'bold' }}>add your email</Box> to recieve order alerts , invoices , payment statements.
                                            </Typography>
                                        )}
                                    </Grid>
                                    <Grid container spacing={3} sx={{ mb: 3 }}>

                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                required fullWidth
                                                label="Phone Number"
                                                name="phoneNumber"
                                                value={formData.phoneNumber}
                                                onChange={handleChange}
                                                error={!!phoneError}
                                                helperText={phoneError || "Include country code (e.g. +1)"}
                                                variant="outlined"
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth label="Company Website" name="companyWebsite" value={formData.companyWebsite} onChange={handleChange} variant="outlined" placeholder="Optional" />
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField required fullWidth multiline rows={2} label="Business Address" name="businessAddress" value={formData.businessAddress} onChange={handleChange} variant="outlined" />
                                    </Grid>

                                    <Divider sx={{ my: 4 }} />

                                    <Typography variant="h6" gutterBottom sx={{ color: '#75a84b', fontWeight: 'bold', mb: 3 }}>
                                        Security
                                    </Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="New Password"
                                                name="password"
                                                type={showPassword ? 'text' : 'password'}
                                                value={formData.password}
                                                onChange={handleChange}
                                                placeholder="Leave empty to keep current"
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </TabPanel>

                                {/* --- Tab 2: Business Info --- */}
                                <TabPanel value={tabValue} index={1}>
                                    <Typography variant="h6" gutterBottom sx={{ color: '#75a84b', fontWeight: 'bold', mb: 3 }}>
                                        Business Operations
                                    </Typography>
                                    <Grid item xs={12} sm={6} sx={{ mb: 3 }}>
                                        <TextField fullWidth label="Business Type" name="businessType" value={formData.businessType} onChange={handleChange} placeholder="e.g. Restaurant, Hotel" />
                                    </Grid>
                                    <Grid item xs={12} sm={6} sx={{ mb: 3 }}>
                                        <TextField fullWidth label="Years Operating" name="yearsOperating" value={formData.yearsOperating} onChange={handleChange} />
                                    </Grid>
                                    <Grid item xs={12} sm={6} sx={{ mb: 3 }}>
                                        <TextField fullWidth label="Number of Locations" name="numLocations" type="number" value={formData.numLocations} onChange={handleChange} />
                                    </Grid>
                                    <Grid container spacing={3}>

                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth label="Estimated Volume" name="estimatedVolume" value={formData.estimatedVolume} onChange={handleChange} placeholder="Weekly/Monthly Order Volume" />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField fullWidth multiline rows={3} label="Additional Notes" name="additionalNotes" value={formData.additionalNotes} onChange={handleChange} placeholder="Any other details about your business..." />
                                        </Grid>
                                    </Grid>
                                </TabPanel>

                                {/* --- Tab 3: Logistics --- */}
                                <TabPanel value={tabValue} index={2}>
                                    <Typography variant="h6" gutterBottom sx={{ color: '#75a84b', fontWeight: 'bold', mb: 3 }}>
                                        Delivery Preferences
                                    </Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <TextField fullWidth multiline rows={2} label="Preferred Delivery Days/Times" name="preferredDeliveryTimes" value={formData.preferredDeliveryTimes} onChange={handleChange} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl component="fieldset">
                                                <FormLabel component="legend">Do you have a loading dock?</FormLabel>
                                                <RadioGroup row name="hasLoadingDock" value={formData.hasLoadingDock} onChange={handleChange}>
                                                    <FormControlLabel value="yes" control={<Radio sx={{ color: '#75a84b', '&.Mui-checked': { color: '#75a84b' } }} />} label="Yes" />
                                                    <FormControlLabel value="no" control={<Radio sx={{ color: '#75a84b', '&.Mui-checked': { color: '#75a84b' } }} />} label="No" />
                                                </RadioGroup>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField fullWidth multiline rows={3} label="Special Delivery Instructions" name="specialDeliveryInstructions" value={formData.specialDeliveryInstructions} onChange={handleChange} placeholder="Gate codes, rear entrance, etc." />
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{ my: 4 }} />

                                    <Typography variant="h6" gutterBottom sx={{ color: '#75a84b', fontWeight: 'bold', mb: 2 }}>
                                        Priorities
                                    </Typography>
                                    <FormControl component="fieldset" fullWidth>
                                        <FormLabel component="legend">What matters most to you?</FormLabel>
                                        <FormGroup row>
                                            {Object.keys(formData.priorities).map((key) => (
                                                <FormControlLabel
                                                    key={key}
                                                    control={
                                                        <Checkbox
                                                            checked={formData.priorities[key]}
                                                            onChange={handleCheckboxChange}
                                                            name={key}
                                                            sx={{ color: '#75a84b', '&.Mui-checked': { color: '#75a84b' } }}
                                                        />
                                                    }
                                                    label={key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim()}
                                                />
                                            ))}
                                        </FormGroup>
                                    </FormControl>
                                </TabPanel>

                                {/* --- Tab 4: Billing --- */}
                                <TabPanel value={tabValue} index={3}>
                                    <Typography variant="h6" gutterBottom sx={{ color: '#75a84b', fontWeight: 'bold', mb: 3 }}>
                                        Billing & Payment
                                    </Typography>
                                    <Grid container spacing={4}>
                                        <Grid item xs={12}>
                                            <FormControl component="fieldset">
                                                <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>Preferred Payment Method</FormLabel>
                                                <RadioGroup name="paymentMethod" value={formData.paymentMethod} onChange={handleChange}>
                                                    <FormControlLabel value="cod" control={<Radio sx={{ color: '#75a84b', '&.Mui-checked': { color: '#75a84b' } }} />} label="Cash on Delivery" />
                                                    <FormControlLabel value="credit_debit_online" control={<Radio sx={{ color: '#75a84b', '&.Mui-checked': { color: '#75a84b' } }} />} label="Credit Card / Debit (Online Secured Payment)" />
                                                    <FormControlLabel value="e_transfer" control={<Radio sx={{ color: '#75a84b', '&.Mui-checked': { color: '#75a84b' } }} />} label="E-Transfer" />
                                                    <FormControlLabel value="cheque" control={<Radio sx={{ color: '#75a84b', '&.Mui-checked': { color: '#75a84b' } }} />} label="Cheque" />
                                                </RadioGroup>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl component="fieldset">
                                                <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>Statement Frequency</FormLabel>
                                                <RadioGroup row name="statement_duration" value={formData.statement_duration} onChange={handleChange}>
                                                    <FormControlLabel value="Daily" control={<Radio sx={{ color: '#75a84b', '&.Mui-checked': { color: '#75a84b' } }} />} label="Daily" />
                                                    <FormControlLabel value="Weekly" control={<Radio sx={{ color: '#75a84b', '&.Mui-checked': { color: '#75a84b' } }} />} label="Weekly" />
                                                    <FormControlLabel value="Semi-Monthly" control={<Radio sx={{ color: '#75a84b', '&.Mui-checked': { color: '#75a84b' } }} />} label="Semi-Monthly" />
                                                    <FormControlLabel value="Monthly" control={<Radio sx={{ color: '#75a84b', '&.Mui-checked': { color: '#75a84b' } }} />} label="Monthly" />
                                                </RadioGroup>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </TabPanel>

                                {/* --- Action Bar --- */}
                                <Box sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                    <Button variant="outlined" onClick={() => navigate('/catalog')} sx={{ color: 'text.secondary', borderColor: 'divider' }}>
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={handleSubmit}
                                        disabled={submitting}
                                        startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                        sx={{
                                            bgcolor: '#75a84b',
                                            color: 'white',
                                            px: 4,
                                            '&:hover': { bgcolor: '#638e3f' }
                                        }}
                                    >
                                        {submitting ? 'Saving...' : 'Save Changes'}
                                    </Button>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>

                {/* --- Mobile Sticky Save Button (optional) --- */}
                {isMobile && (
                    <Fab
                        color="primary"
                        aria-label="save"
                        onClick={handleSubmit}
                        sx={{
                            position: 'fixed',
                            bottom: 24,
                            right: 24,
                            bgcolor: '#75a84b',
                            '&:hover': { bgcolor: '#638e3f' }
                        }}
                    >
                        {submitting ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
                    </Fab>
                )}
            </Box>
        </PageAnimation>
    );
}

export default CompleteRegistrationPage;
